import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/Button";
import type { Table } from "../../types";
import { getMaxTableSeats } from "../../services/restaurantSettings";

export interface TableFormValues {
  number: number;
  seats: number;
  zone: string;
  status: Table["status"];
}

interface TableFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: TableFormValues) => Promise<void> | void;
  initialValues?: Table | null;
  isSubmitting?: boolean;
  existingZones?: string[];
  /** Optional preferred zone to prefill when creating a new table */
  initialZone?: string | null;
  /** Max allowed seats per table (global setting) */
  maxSeatsLimit?: number;
}

const STATUS_OPTIONS: Table["status"][] = ["available", "reserved", "occupied"];

const defaultValues: TableFormValues = {
  number: 1,
  seats: 2,
  zone: "",
  status: "available",
};

type ZoneMode = "existing" | "new";

const TableForm: React.FC<TableFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues = null,
  isSubmitting = false,
  existingZones = [],
  initialZone = null,
  maxSeatsLimit,
}) => {
  const maxTableSeats = maxSeatsLimit ?? getMaxTableSeats();
  const [values, setValues] = useState<TableFormValues>(defaultValues);
  const [error, setError] = useState<string | null>(null);
  const [zoneMode, setZoneMode] = useState<ZoneMode>("existing");
  const [newZone, setNewZone] = useState("");

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setValues({
          number: initialValues.number,
          seats: initialValues.seats,
          zone: initialValues.zone,
          status: initialValues.status,
        });
        if (existingZones.includes(initialValues.zone)) {
          setZoneMode("existing");
          setNewZone("");
        } else {
          setZoneMode("new");
          setNewZone(initialValues.zone);
        }
      } else {
        const zones = existingZones.filter(Boolean);
        if (typeof initialZone === "string" && initialZone.trim().length > 0) {
          // Explicit zone requested (e.g. from "Νέα ζώνη" in Zones section)
          setValues({
            ...defaultValues,
            zone: initialZone,
          });
          // Treat as "new" so it doesn't have to exist in the list yet
          setZoneMode("new");
          setNewZone(initialZone);
        } else if (zones.length > 0) {
          setValues({
            ...defaultValues,
            zone: zones[0],
          });
          setZoneMode("existing");
          setNewZone("");
        } else {
          setValues({
            ...defaultValues,
            zone: "",
          });
          setZoneMode("new");
          setNewZone("");
        }
      }
      setError(null);
    }
  }, [open, initialValues, existingZones, initialZone]);

  const zoneOptions = useMemo(() => {
    const zones = new Set(existingZones.filter(Boolean).map((zone) => zone.trim()));
    return Array.from(zones).sort();
  }, [existingZones]);

  const handleInputChange = (field: keyof TableFormValues) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = event.target;
    setValues((prev) => ({
      ...prev,
      [field]:
        field === "number" || field === "seats"
          ? value === ""
            ? ("" as unknown as number)
            : Number(value)
          : value,
    }));
  };

  const validate = (data: TableFormValues) => {
    if (!Number.isFinite(data.number) || data.number < 1) {
      return "Ο αριθμός τραπεζιού πρέπει να είναι θετικός αριθμός.";
    }
    if (!Number.isFinite(data.seats) || data.seats < 1) {
      return "Ο αριθμός θέσεων πρέπει να είναι τουλάχιστον 1.";
    }
    if (data.seats > maxTableSeats) {
      return `Ο αριθμός θέσεων δεν μπορεί να ξεπερνά τις ${maxTableSeats}.`;
    }
    if (!data.zone.trim()) {
      return "Πρέπει να ορίσετε ζώνη.";
    }
    if (!STATUS_OPTIONS.includes(data.status)) {
      return "Μη έγκυρη κατάσταση τραπεζιού.";
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const resolvedZone =
      zoneMode === "existing" ? values.zone.trim() : newZone.trim();

    const parsedValues: TableFormValues = {
      number: Number(values.number),
      seats: Number(values.seats),
      zone: resolvedZone,
      status: values.status,
    };

    const validationError = validate(parsedValues);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await onSubmit(parsedValues);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Παρουσιάστηκε σφάλμα κατά την αποθήκευση."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()}>
        <form className="space-y-5" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {initialValues ? "Επεξεργασία τραπεζιού" : "Νέο τραπέζι"}
            </DialogTitle>
            <DialogDescription>
              Ορίστε βασικά χαρακτηριστικά για το τραπέζι.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1 text-sm text-card-foreground">
              <span>Αριθμός τραπεζιού</span>
              <input
                type="number"
                min={1}
                value={values.number}
                onChange={handleInputChange("number")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </label>

            <label className="space-y-1 text-sm text-card-foreground">
              <span>Θέσεις</span>
              <input
                type="number"
                min={1}
                max={maxTableSeats}
                value={values.seats}
                onChange={handleInputChange("seats")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              />
            </label>
          </div>

          <div className="space-y-3">
            <label className="space-y-1 text-sm text-card-foreground w-full">
              <span>Ζώνη</span>
              <select
                value={zoneMode === "existing" ? values.zone : "__new__"}
                onChange={(event) => {
                  const { value } = event.target;
                  if (value === "__new__") {
                    setZoneMode("new");
                    setNewZone("");
                    setValues((prev) => ({ ...prev, zone: "" }));
                  } else {
                    setZoneMode("existing");
                    setNewZone("");
                    setValues((prev) => ({ ...prev, zone: value }));
                  }
                }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {zoneOptions.length === 0 && (
                  <option value="__new__">+ Νέα ζώνη</option>
                )}
                {zoneOptions.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
                <option value="__new__">+ Νέα ζώνη…</option>
              </select>
            </label>

            {zoneMode === "new" && (
              <label className="space-y-1 text-sm text-card-foreground w-full">
                <span>Όνομα νέας ζώνης</span>
                <input
                  value={newZone}
                  onChange={(event) => setNewZone(event.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="π.χ. Βεράντα"
                  required
                />
              </label>
            )}
          </div>

          <label className="space-y-1 text-sm text-card-foreground w-full">
            <span>Κατάσταση</span>
            <select
              value={values.status}
              onChange={handleInputChange("status")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {STATUS_OPTIONS.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {statusOption === "available" && "Διαθέσιμο"}
                  {statusOption === "reserved" && "Κρατημένο"}
                  {statusOption === "occupied" && "Κατειλημμένο"}
                </option>
              ))}
            </select>
          </label>

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Ακύρωση
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Αποθήκευση..." : "Αποθήκευση"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TableForm;

